import { useMemo } from 'react';
import { useQuery } from 'convex/react';

import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useAuth } from './useAuth';

/**
 * Reactive job snapshot.
 *
 * Subscribes to:
 *   - jobs.get          → state, error, timestamps
 *   - agentOutputs.listForJob → all outputs across all agents
 *   - artifacts.listForJob    → hi-fi images, asset images, code zip
 *
 * Returns a typed shape every pipeline panel can consume directly.
 */

export type AgentId = 'ora' | 'scry' | 'loom' | 'lex' | 'lumen' | 'mosaic' | 'forge';

export type ReviewStatus =
  | 'pending'
  | 'awaiting_user'
  | 'approved'
  | 'revising'
  | 'superseded'
  | 'skipped';

export interface AgentOutputRow {
  _id: string;
  agent: AgentId;
  itemKey: string | null;
  iteration: number;
  payload: unknown;
  userEdits: unknown;
  reviewStatus: ReviewStatus;
  userFeedback: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  latencyMs: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface ArtifactRow {
  _id: string;
  kind: 'hifi_page_image' | 'asset_image' | 'code_zip';
  filename: string;
  contentType: string;
  bytes: number;
  ref: string | null;
  url: string | null;
  createdAt: number;
}

export interface JobProgress {
  loading: boolean;
  notFound: boolean;
  state: string;
  error: string | null;
  statusLine: string | null;
  progressPct: number | null;
  currentAgent: string | null;
  outputs: AgentOutputRow[];
  artifacts: ArtifactRow[];
  /** Latest non-superseded output for a given (agent, itemKey). */
  latestFor: (agent: AgentId, itemKey?: string | null) => AgentOutputRow | undefined;
  /** All outputs for a given agent, latest-iteration only per itemKey. */
  latestPerItem: (agent: AgentId) => AgentOutputRow[];
}

function jobScopedTokenArgs(
  token: string | null | undefined,
  jobId: Id<'jobs'> | null,
): { token: string; jobId: Id<'jobs'> } | 'skip' {
  if (typeof token !== 'string' || !token || !jobId) return 'skip';
  return { token, jobId };
}

export function useJobProgress(jobId: Id<'jobs'> | null): JobProgress {
  const { token } = useAuth();

  const jobSub = useMemo(
    () => jobScopedTokenArgs(token, jobId),
    [token, jobId],
  );
  const job = useQuery(api.jobs.get, jobSub);
  const outputs = useQuery(api.agentOutputs.listForJob, jobSub);
  const artifacts = useQuery(api.artifacts.listForJob, jobSub);

  return useMemo<JobProgress>(() => {
    const loading = job === undefined || outputs === undefined || artifacts === undefined;
    const notFound = job === null;

    const rows = (outputs ?? []) as AgentOutputRow[];
    const arts = (artifacts ?? []) as ArtifactRow[];

    const latestFor = (agent: AgentId, itemKey: string | null = null) => {
      // Walk in reverse to find the latest matching row.
      for (let i = rows.length - 1; i >= 0; i--) {
        const r = rows[i];
        if (r.agent !== agent) continue;
        if ((r.itemKey ?? null) !== itemKey) continue;
        if (r.reviewStatus === 'superseded') continue;
        return r;
      }
      return undefined;
    };

    const latestPerItem = (agent: AgentId) => {
      const seen = new Map<string, AgentOutputRow>();
      // Walk in order; later writes for the same itemKey overwrite earlier.
      for (const r of rows) {
        if (r.agent !== agent) continue;
        if (r.reviewStatus === 'superseded') continue;
        seen.set(r.itemKey ?? '', r);
      }
      return Array.from(seen.values()).sort((a, b) => a.createdAt - b.createdAt);
    };

    return {
      loading,
      notFound,
      state: job?.state ?? '',
      error: job?.errorMessage ?? null,
      statusLine: job?.statusLine ?? null,
      progressPct: job?.progress ?? null,
      currentAgent: job?.currentAgent ?? null,
      outputs: rows,
      artifacts: arts,
      latestFor,
      latestPerItem,
    };
  }, [job, outputs, artifacts]);
}
